import { Cast } from '@/modules/casts/classes'
import store from '@/store'
import { KeyDict } from '@/types'
import { AssetType } from '@/types/assets'
import { contentSortByOrder, SceneContent } from '@/types/sceneContent'
import { ClipEndAction } from '@/types/videoplayer'
import { computed, watch } from '@vue/composition-api'

const getSortedClipContentsInCast = (cast: Cast) => {
  const allClips: KeyDict<SceneContent> = {}
  for (const scene of Object.values(cast.scenes)) {
    for (const content of Object.values(scene.contents)) {
      if (content.type !== AssetType.Video || allClips[content.id] !== undefined) continue
      allClips[content.id] = content
    }
  }
  return Object.values(allClips).sort(contentSortByOrder)
}

const getNewClipOrders = (sortedClips: SceneContent[]) => {
  const clipOrders: KeyDict<number> = {}
  let newOrder = 0
  for (const clip of sortedClips) {
    clipOrders[clip.id] = newOrder
    ++newOrder
  }
  return clipOrders
}

const updateOrderOfClips = (cast: Cast, sortedClips: SceneContent[]) => {
  const clipOrders = getNewClipOrders(sortedClips)
  for (const scene of Object.values(cast.scenes)) {
    for (const [contentId, content] of Object.entries(scene.contents)) {
      const newOrder: number|undefined = clipOrders[contentId]
      if (newOrder === undefined) continue
      content.order = newOrder
    }
  }
}

const updateActiveClip = (cast: Cast, sortedClips: SceneContent[]) => {
  const hasActiveClip = sortedClips.some((content) => content.active)
  if (hasActiveClip) return
  const clipToActivate = sortedClips[0]?.id ?? null
  if (clipToActivate === null) return
  cast.updateActiveClip(clipToActivate)
}

const removeClipFromCurrentCast = async (clipContentId: string) => {
  let updateClipInfo = null as { clipId: string|null, endAction: ClipEndAction}|null
  const result = await store.dispatch.events.updateCurrentCast((cast) => {
    const removeResult = cast.removeContentsFromScenes([clipContentId])
    if (removeResult.isFailure) return removeResult.convert()
    const sortedClips = getSortedClipContentsInCast(cast)
    updateOrderOfClips(cast, sortedClips)
    updateActiveClip(cast, sortedClips)
    if (store.state.videoPlayer.currentSelectedClip?.player1 === clipContentId) {
      updateClipInfo = {
        clipId: sortedClips[0]?.id ?? null,
        endAction: sortedClips[0]?.clip_end_action ?? ClipEndAction.Next
      }
    }
    return cast
  })
  if (result.isSuccess && updateClipInfo !== null) {
    if (updateClipInfo.endAction === ClipEndAction.Loop && updateClipInfo.clipId !== null) {
      await store.dispatch.videoPlayer.playAsset({ playerName: 'player1', assetId: updateClipInfo.clipId })
    } else {
      await store.dispatch.videoPlayer.setCurrentSelectedClip({ player: 'player1', assetId: updateClipInfo.clipId })
    }
  }
  return result
}

const setupLazyUpdateActiveClipWatch = (playerId: string) => {
  // Note: This assumes that some other composition/component subscribed to the specific videoPlayer.
  const currentSelectedClipId = computed(() => {
    return store.state.videoPlayer.currentSelectedClip[playerId] ?? null
  })
  watch(currentSelectedClipId, async (newClipId: string|null) => {
    const result = await store.dispatch.events.updateCurrentCast((cast) => cast.updateActiveClip(newClipId))
    result.logIfError(`Failed to set active clip to ${newClipId}`)
  })
}

const useScenePaletteClips = () => {
  setupLazyUpdateActiveClipWatch('player1')

  return {
    removeClipFromCurrentCast
  }
}

export default useScenePaletteClips
